// TRADING v2 — batch 5's rebuild of TradingReport (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on).
//
// Astra's screen (02-screen-review/trading) under the operator's rulings of 26 Oct 2026: the title over the period it
// reads, the reports' tabs, the answer for the period (a strip), then ONE analysis at a time — Cost, price & margin by
// inventory book (the default), Trade-type mix, or By party — chosen in one select beside the period, every analysis
// over the same period and the same party. A book's run by day / week / month / year opens behind its row; the rules
// behind "Basis & supporting detail". Read-only: no button but Try again, the "N more" of a long list, and the party
// filter's clear.
//
// THE PERIOD NOW SCOPES ALL THREE. Today's screen asked the route for all time and labelled Mix and By party "All time"
// whatever "Group by" said; the route filters all three by `from` / `to`, so here the period chosen is the period every
// table reads, and the head says which.
//
// KEPT FROM TODAY'S SCREEN (screens/TradingReport.tsx; its rules copied into components/v2/reports-bs/trading.tsx): the
// route and the 'trading-report' query key, the party filter (the "All parties" drop-down, or a party pressed in By party —
// restored 1 Nov 2026 with Group by, Deals and Margin bps), AED and INR never summed (the strip is per currency unless one book is in view), trading margin and cheque
// fees apart, "—" for a margin not realised, "incl. short cover", the mix in the customer's words, "(own trades)",
// bps, the bucket's labels (Dubai days, "Week of", months, years), a failed read as a red line with Try again and never
// the quiet period's line, an empty claim only from a successful read, the Worker's note. NEW: By party says when it is
// the route's top 50 (the route cuts there), and shows the first 10 with "N more".
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import type { TradingPartyRow, TradingReportResponse, TradingSeriesRow } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { fetchInventory, type BasisSource } from '../../components/v2/reports-pl/inventory-data';
import { dubaiDate, fmtDay } from '../../lib/format';
import { partyName, sameAsset } from '../../lib/naming';
import { periodDays, type PeriodValue } from '../../components/PeriodChips';
import { PeriodDates, PeriodSelect } from '../../components/v2/trades/PeriodSelect';
import { EmptyState, Notice, PageHead, Panel, ReadFailed, Register, SkeletonRows, SummaryStrip, TAP, ReadNotice } from '../../components/v2/kit';
import { ReportTabs } from '../../components/v2/reports-bs/ReportTabs';
import {
  ANALYSES, BookDetail, MIX_COLUMNS, bookKey, bucketLabel, costCard, costColumns, mixCard, partyCard, partyColumns, stripItems, type Analysis, type BasisOf, type Bucket,
} from '../../components/v2/reports-bs/trading';

/** The period in the head's words: "1 Oct – 26 Oct 2026", one day as its date, "All time". */
function periodWords(p: PeriodValue): string {
  if (p.key === 'all') return 'All time';
  const { from, to } = periodDays(p);
  const end = to ?? dubaiDate();
  // No current year (HOUSE-STYLE rule 11): "01 Oct – 26 Oct"; another year keeps it.
  const y = end.slice(0, 4) !== dubaiDate().slice(0, 4);
  if (!from) return `To ${fmtDay(end, y)}`;
  return from === end ? fmtDay(from, y) : `${fmtDay(from, from.slice(0, 4) !== end.slice(0, 4))} – ${fmtDay(end, y)}`;
}

const PARTY_PAGE = 10;
/** Inventory's pill words for a cost that is not measured (screens/v2/Inventory.tsx BASIS_BADGE). */
const BASIS_WORD: Record<BasisSource, string> = { measured: 'Measured', operator: 'Your figure', peg: 'Assumed at peg', provisional: 'Provisional', unset: 'No cost set' };
const GROUPS: ReadonlyArray<readonly [Bucket, string]> = [['all', 'Whole period'], ['day', 'By day'], ['week', 'By week'], ['month', 'By month'], ['year', 'By year']];
/** The route's cap on By party (`LIMIT 50`): a full page is the top 50 by margin, never all. */
const PARTY_CAP = 50;

export function TradingV2() {
  const [params, setParams] = useSearchParams();
  const view = (ANALYSES.some(([k]) => k === params.get('view')) ? params.get('view') : 'cost') as Analysis;
  const party = params.get('party') ?? '';
  // GROUP BY (the old screen's chips, across every book): the period whole, or its days, weeks, months or years — By book only.
  const bucket = (GROUPS.some(([k]) => k === params.get('group')) ? params.get('group') : 'all') as Bucket;
  const book = params.get('book');
  const [period, setPeriod] = useState<PeriodValue>({ key: 'month' });
  const [allParties, setAllParties] = useState(false);
  const { from, to } = periodDays(period);
  const words = periodWords(period);

  const q = useQuery<TradingReportResponse>({
    queryKey: ['trading-report', view === 'cost' ? bucket : 'all', party, from ?? '', to ?? ''],
    queryFn: () => api.tradingReport({ bucket: view === 'cost' ? bucket : 'all', from, to, ...(party ? { party } : {}) }),
  });
  const rs = readState(q);
  const retry = () => { void q.refetch(); };

  const setParam = useCallback((k: string, v: string | null, replace = false) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set(k, v); else n.delete(k);
    return n;
  }, { replace }), [setParams]);
  const openBook = useCallback((r: TradingSeriesRow) => setParam('book', bookKey(r)), [setParam]);
  const closeBook = useCallback(() => setParam('book', null, true), [setParam]);
  // Pressing a party filters every analysis to it, and shows its books.
  const pickParty = useCallback((p: TradingPartyRow) => setParams((cur) => {
    const n = new URLSearchParams(cur); n.set('party', p.party_slug); n.delete('view'); return n;
  }), [setParams]);

  // THE COST BASIS FROM INVENTORY (Astra, 1 Nov 2026): a book whose cost is assumed or missing has an estimated margin —
  // neutral, labelled, its why on Inventory. Inventory's own read and key; not answered yet, the margins stay as they are.
  const inv = useQuery({ queryKey: ['inventory', 'all', null, null], queryFn: () => fetchInventory('all', {}) });
  const basisOf = useMemo<BasisOf>(() => (asset, ccy) => {
    const b = inv.data?.books.find((x) => !x.retired && sameAsset(x.asset, asset) && x.quote_currency === ccy);
    return b && (b.basis.assumed || b.basis.source === 'unset') ? BASIS_WORD[b.basis.source] : null;
  }, [inv.data]);
  const when = useMemo(() => (bucket === 'all' ? words : (r: TradingSeriesRow) => bucketLabel(r.period, bucket)), [bucket, words]);
  const cost = useMemo(() => costColumns(when, basisOf), [when, basisOf]);
  const costCardOf = useMemo(() => costCard(when, basisOf), [when, basisOf]);
  // A party's margin in a currency whose book rests on an assumed or missing cost is an estimate too.
  const basisCcy = useMemo(() => (ccy: string) => {
    const b = inv.data?.books.find((x) => !x.retired && x.quote_currency === ccy && (x.basis.assumed || x.basis.source === 'unset'));
    return b ? BASIS_WORD[b.basis.source] : null;
  }, [inv.data]);
  const partyCols = useMemo(() => partyColumns(basisCcy), [basisCcy]);
  const partyCardOf = useMemo(() => partyCard(basisCcy), [basisCcy]);
  // THE PARTY DROP-DOWN (the old screen's "All parties"): the parties an unfiltered read named, remembered while one is picked.
  const [partyOptions, setPartyOptions] = useState<string[]>([]);
  useEffect(() => {
    if (!party && q.data) setPartyOptions([...new Set(q.data.by_party.map((p) => p.party_slug))].sort((a, b) => partyName({ slug: a }).localeCompare(partyName({ slug: b }))));
  }, [party, q.data]);

  const head = (
    <>
      <PageHead title="Trading" sub={words} />
      <ReportTabs current="/trading" />
    </>
  );

  const controls = (
    <div className="mb-4 flex flex-wrap items-end gap-2 lg:mb-5">
      <div className="flex w-full min-w-0 gap-2 sm:w-auto">
        <PeriodSelect value={period} onChange={setPeriod} />
        <label className="min-w-0 flex-1 sm:flex-none">
          <span className="sr-only">Analysis</span>
          <select className="select w-full sm:w-auto" value={view} onChange={(e) => setParam('view', e.target.value === 'cost' ? null : e.target.value)}>
            {ANALYSES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
      </div>
      {view === 'cost' && (
        <label className="min-w-0 flex-1 sm:flex-none">
          <span className="sr-only">Group by</span>
          <select className="select w-full sm:w-auto" value={bucket} onChange={(e) => setParam('group', e.target.value === 'all' ? null : e.target.value)}>
            {GROUPS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
      )}
      {(partyOptions.length > 0 || party) && (
        <label className="min-w-0 flex-1 sm:flex-none">
          <span className="sr-only">Party</span>
          <select className="select w-full sm:w-auto sm:max-w-[18rem]" value={party} onChange={(e) => setParam('party', e.target.value || null)}>
            <option value="">All parties</option>
            {(party && !partyOptions.includes(party) ? [party, ...partyOptions] : partyOptions).map((p) => <option key={p} value={p}>{partyName({ slug: p })}</option>)}
          </select>
        </label>
      )}
    </div>
  );

  if (rs === 'failed' && !q.data) {
    return <>{head}{controls}<PeriodDates value={period} onChange={setPeriod} /><ReadNotice what="trading" error={q.error} onRetry={retry} /></>;
  }
  if (!q.data) {
    return (
      <>
        {head}{controls}<PeriodDates value={period} onChange={setPeriod} />
        {q.fetchStatus === 'paused' && <Notice tone="neutral">Offline — waiting to read.</Notice>}
        <Panel flush><SkeletonRows rows={3} what="trading" /></Panel>
      </>
    );
  }

  const data = q.data;
  const known = rs === 'known';
  const quiet = data.series.length === 0 && data.mix.length === 0 && data.by_party.length === 0;
  const partyCut = data.by_party.length >= PARTY_CAP;
  const parties = allParties ? data.by_party : data.by_party.slice(0, PARTY_PAGE);
  const hidden = data.by_party.length - parties.length;
  const count = view === 'cost' ? data.series.length : view === 'mix' ? data.mix.length : data.by_party.length;
  const countWords = view === 'cost' ? (bucket !== 'all' ? (count === 1 ? 'row' : 'rows') : count === 1 ? 'book' : 'books') : view === 'mix' ? (count === 1 ? 'type' : 'types') : (count === 1 ? 'party' : 'parties');

  return (
    <>
      {head}
      {/* A failed refresh keeps the rows it had and says so, once; nothing below claims "nothing" from them. */}
      {rs === 'failed' && <Notice tone="warn"><ReadFailed what="trading" error={q.error} onRetry={retry} /></Notice>}

      {/* The By book table already shows each book's margin, deals and basis: the strip would say it twice (Astra, 1 Nov 2026). */}
      {view !== 'cost' && data.series.length > 0 && <SummaryStrip phoneCols={data.series.length === 1 ? 2 : 1} items={stripItems(data.series, basisOf)} />}
      {controls}
      <PeriodDates value={period} onChange={setPeriod} />

      {quiet ? (
        known && <Panel><EmptyState>Nothing in this period.</EmptyState></Panel>
      ) : (
        <Panel flush>
          {count === 0 && known && <EmptyState className="px-4">Nothing in this period.</EmptyState>}
          {view === 'cost' && data.series.length > 0 && (
            <Register label="Cost, price and margin by inventory book" tableFrom="xl" columns={cost} rows={data.series} rowKey={(r) => `${r.period ?? 'all'}|${bookKey(r)}`} onOpen={openBook} card={costCardOf} />
          )}
          {view === 'mix' && data.mix.length > 0 && (
            <Register label="Trade-type mix" columns={MIX_COLUMNS} rows={data.mix} rowKey={(m) => `${m.trade_class}|${m.quote_ccy}`} card={mixCard} />
          )}
          {view === 'party' && parties.length > 0 && (
            <Register label="By party" columns={partyCols} rows={parties} rowKey={(p) => `${p.party_slug}|${p.quote_ccy}`} onOpen={pickParty} card={partyCardOf} />
          )}
          {count > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
              <span className="flex items-center gap-4">
                {known && <span>{view === 'party' && partyCut ? `Top ${data.by_party.length} by margin` : `${count} ${countWords}`}</span>}
                {view === 'party' && hidden > 0 && <button type="button" className={TAP} onClick={() => setAllParties(true)}>{hidden} more</button>}
              </span>
              <span>Dubai time</span>
            </div>
          )}
        </Panel>
      )}

      <details className="group mt-4 border-t border-stroke pt-4 lg:mt-5">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-1 text-body text-tg-hint lg:min-h-[40px] [&::-webkit-details-marker]:hidden"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />Basis &amp; supporting detail</summary>
        <div className="mt-2 space-y-2 rounded-[10px] border border-stroke bg-panel p-4 text-body text-tg-hint lg:max-w-3xl lg:p-5">
          <p>Per deal: the moving-average cost of what was sold, what it sold for, and the margin between them, over the period chosen.</p>
          <p>AED and INR are never summed. The desk runs two books with separate costs and there is no FX feed, so a blended margin would be an invented rate.</p>
          <p>A cheque earns a fee, not a margin on inventory — the two are kept apart so the trading figures say how well the desk traded.</p>
          <p>
            Share of volume and share of margin answer different questions and often disagree. A customer sale to the desk normally realises
            no margin: it builds inventory, and buying below the average lowers the average so the gain lands when the stock is sold. The
            exception is a buy that covers a short, which does realise and is flagged where it happens.
          </p>
          <p>A margin the engine did not realise reads &ldquo;—&rdquo;, never 0.00. Open a book to see it by day, week, month or year.</p>
          {data.note && <p>{data.note}</p>}
        </div>
      </details>

      {book && <BookDetail key={`${book}|${party}|${from}|${to}`} book={book} period={words} from={from} to={to} party={party} onClose={closeBook} />}
    </>
  );
}
